import { forwardRef } from 'react';
import { ScenarioRunState } from '../../../api/api';
import { Outcome, outcomeOfScenario } from '../ScenarioRunTable';
import { ScenarioRunStatus } from '../ScenarioRunStatus';
import { StopRunButton } from '../StopRunButton';
import { ExactLimits } from './ExactLimits';
import { LimitsView } from './exactLimitsModel';
import { isInRun } from './runDetailModel';

/** The environment a run tests, as a pill: the one thing that must never be misread. */
export const EnvironmentPill = ({ target }: { target?: string }) =>
    target ? (
        <span className="rd-env" title="The environment this run tests">
            {target}
        </span>
    ) : null;

/**
 * The head of the run, kept at the top of the screen once the page has scrolled past the real one: which suite, how
 * far, where, the stop button and Exact's limits.
 */
export const RunStickyBar = forwardRef<
    HTMLDivElement,
    {
        state: ScenarioRunState;
        visible: boolean;
        finished: boolean;
        canStop: boolean;
        customer: string;
        environment: string;
        runId: string;
        limits?: LimitsView;
    }
>(({ state, visible, finished, canStop, customer, environment, runId, limits }, ref) => {
    const outcomes = (state.scenarios ?? [])
        .filter(isInRun)
        .map((scenario) => outcomeOfScenario(scenario).outcome as Outcome);
    const count = (...kinds: Outcome[]) => outcomes.filter((outcome) => kinds.includes(outcome)).length;

    const counts = [
        { label: 'running', value: count('running'), kind: 'busy' },
        { label: 'failed', value: count('failed'), kind: 'failed' },
        { label: 'passed', value: count('passed', 'warnings'), kind: 'passed' },
        { label: finished ? 'not run' : 'to go', value: count('pending', 'skipped'), kind: 'idle' },
    ].filter((entry) => entry.value > 0);

    return (
        <div ref={ref} className={`rd-sticky${visible ? ' rd-sticky--visible' : ''}`} aria-hidden={!visible}>
            <div className="container rd-sticky__inner">
                <span className="rd-sticky__title">
                    <span className="rd-sticky__suite">{state.suiteName}</span>
                    <ScenarioRunStatus status={state.status} />
                    <EnvironmentPill target={state.target} />
                </span>
                <ul className="rd-sticky__counts">
                    {counts.map((entry) => (
                        <li key={entry.label} className={`rd-count rd-count--${entry.kind}`}>
                            <span className="rd-count__value">{entry.value}</span> {entry.label}
                        </li>
                    ))}
                </ul>
                <ExactLimits limits={limits} running={!finished} compact />
                {canStop && !finished && (
                    <StopRunButton customer={customer} environment={environment} runId={runId} compact />
                )}
            </div>
        </div>
    );
});

RunStickyBar.displayName = 'RunStickyBar';
